/**
 * «Контакти» — довідник зовнішніх контактів станції: екстрені служби,
 * контрагенти, учасники ринку, державні органи.
 *
 * ОКРЕМА СТОРІНКА, а не переробка /counterparties. Пункт меню «Контакти» раніше
 * вів на список контрагентів; той список нікуди не подівся й лишається робочим
 * за своєю адресою — просто більше не висить у меню. Так стара інформація
 * прихована, але не видалена, як і просили.
 *
 * Тексти українською просто в коді, без i18n: це назви українських установ і
 * телефони українських служб, перекладати їх нема куди. Той самий підхід уже
 * вжитий на сусідній сторінці новин (InfoRedesign).
 *
 * Телефони — посилання tel:, щоб з планшета в полі дзвонити одним дотиком.
 * Номери чергового диспетчера й аварійної служби ОСР у документі не заповнені
 * (плейсхолдери «+38 ___ ___ __ __»), тож показані як «не вказано» — вигадувати
 * їх не можна, а робити вигляд, що номер є, небезпечно.
 */

import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { pageMaxClass } from '@/utils/pageWidth';
import { useState, type ReactElement } from 'react';
import { useTheme } from '@/hooks/useTheme';
import HeliosStrategiaMark from './HeliosStrategiaMark';
import './contacts.css';
import {
  Banknote, Building2 as Ministry, Gavel, Handshake, Leaf, Scale as Balance,
  ShieldCheck, Sun, TrendingUp, Waypoints, Wrench, Share2,
  AlertTriangle, Building2, Flame, Landmark, Phone, ShieldAlert, ExternalLink,
  type LucideIcon,
} from 'lucide-react';

interface EmergencyItem {
  label: string;
  hint?: string;
  phone: string | null;
  Icon: LucideIcon;
  /** Критичні служби виділяємо кольором — їх шукають поглядом у стресі. */
  urgent?: boolean;
}

interface OrgItem {
  /** Роль контрагента; у державних органів ролі немає — лише назва. */
  role?: string;
  /**
   * Знак організації — іконка за її ФУНКЦІЄЮ, а не спроба відтворити чужий
   * логотип. Оператор передачі — вузли мережі, регулятор — молоток,
   * гарантований покупець — рукостискання, банк — банкнота. Читається без
   * підпису й лишається монохромним у будь-якій темі.
   */
  Logo: LucideIcon;
  /**
   * Справжній логотип організації, якщо він у нас є (public/logos/).
   *
   * Зібрано 13 із 14: із сайтів організацій, а для тих, що закриті
   * Cloudflare (Укренерго, ДСНС), — з Вікімедіа. Закарпаттяобленерго
   * дістали через їхню 404-сторінку: її віддає сам сайт, а не Cloudflare,
   * і в ній є шапка з посиланням на logo.svg у темі WordPress.
   */
  logoSrc?: string;
  /**
   * Логотип із ВЛАСНИМ непрозорим полотном (а не знак на прозорому тлі).
   *
   * Таких троє: Ощадбанк (полотно непрозоре на 100%), Оператор ринку
   * (білі заливки з градієнтами) і Закарпаттяобленерго (кольоровий щит).
   * Їх не можна
   * зводити в силует — увесь прямокутник став би однією рівною плямою, що
   * й сталось у першій версії. Їм лишається просте знебарвлення — без
   * підкладки: біле полотно вже вшите в самі файли.
   */
  logoFlat?: boolean;
  /**
   * Знак, який малюється розміткою, а не файлом. Потрібен «Геліос Стратегії»:
   * їхній сайт під Cloudflare, тож знак зібраний на сітці знака «Геліос
   * Агрегатора» (див. HeliosStrategiaMark).
   */
  LogoMark?: () => ReactElement;
  /**
   * Фірмові кольори організації (1–2) — фон картки, що відкривається маскою
   * при наведенні разом із кольором логотипа. Це ЧУЖІ брендові кольори з
   * логотипів і сайтів, а не кольори кабінету, тому вони літеральні, а не
   * токени; показуються лише напівпрозоро поверх поверхні плитки.
   */
  brand?: [string, string?];
  /**
   * Колір, яким «розфарбувати» логотип при наведенні, якщо сам файл без
   * кольору (білий/сірий тризуб). Кольоровий шар тоді — не <img>, а маска
   * з файлу, залита цим кольором; працює для будь-якого знака на прозорому тлі.
   */
  logoTint?: string;
  name: string;
  url: string;
}

const EMERGENCY: EmergencyItem[] = [
  { get label() { return i18n.t('ak_contacts_duty_dispatcher'); }, phone: null, Icon: Phone, urgent: true },
  { get label() { return i18n.t('ak_contacts_dso_emergency_service'); }, phone: null, Icon: AlertTriangle, urgent: true },
  { get label() { return i18n.t('ak_contacts_state_emergency_service'); }, get hint() { return i18n.t('ak_contacts_fire_smoke'); }, phone: '101', Icon: Flame, urgent: true },
  { get label() { return i18n.t('ak_contacts_police'); }, get hint() { return i18n.t('ak_contacts_break_in_theft'); }, phone: '102', Icon: ShieldAlert, urgent: true },
];

const COUNTERPARTIES: OrgItem[] = [
  { get role() { return i18n.t('ak_contacts_operations_management'); }, Logo: Sun, logoSrc: '/logos/helios.svg', brand: ['#CC181B', '#F2A93B'], get name() { return i18n.t('ak_contacts_helios_aggregator_llc'); }, url: 'https://heliosaggregate.com' },
  { get role() { return i18n.t('ak_contacts_technical_maintenance'); }, Logo: Wrench, LogoMark: HeliosStrategiaMark, brand: ['#CC181B', '#F2A93B'], get name() { return i18n.t('ak_contacts_helios_strategia_llc'); }, url: 'https://heliosstrategia.com/uk/' },
  { get role() { return i18n.t('ak_contacts_trading'); }, Logo: TrendingUp, logoSrc: '/logos/tepla.png', brand: ['#FF7A1A', '#FFB347'], logoTint: '#FF8C1A', get name() { return i18n.t('ak_contacts_tepla_llc'); }, url: 'https://tepla.ua' },
  { get role() { return i18n.t('ak_contacts_aggregating_group'); }, Logo: Sun, logoSrc: '/logos/helios.svg', brand: ['#CC181B', '#F2A93B'], get name() { return i18n.t('ak_contacts_helios_aggregator_llc'); }, url: 'https://heliosaggregate.com' },
  { get role() { return i18n.t('ak_contacts_servicing_bank'); }, Logo: Banknote, logoSrc: '/logos/oschad.png', logoFlat: true, brand: ['#00463F', '#E3E13C'], get name() { return i18n.t('ak_contacts_oschadbank_jsc'); }, url: 'https://www.oschadbank.ua' },
];

const MARKET: OrgItem[] = [
  { get role() { return i18n.t('ak_contacts_transmission_system_operator'); }, Logo: Waypoints, logoSrc: '/logos/ukrenergo.png', brand: ['#4F62A8', '#7B8BC9'], get name() { return i18n.t('ak_contacts_npc_ukrenergo'); }, url: 'https://ua.energy' },
  { get role() { return i18n.t('ak_contacts_distribution_system_operator'); }, Logo: Share2, logoSrc: '/logos/zakarpat.svg', logoFlat: true, brand: ['#12AD03', '#FFED00'], get name() { return i18n.t('ak_contacts_zakarpattiaoblenergo_pjsc'); }, url: 'https://zakarpat.energy' },
  { Logo: Balance, logoSrc: '/logos/oree.svg', logoFlat: true, brand: ['#20A6DF', '#EE7326'], get name() { return i18n.t('ak_contacts_market_operator_se'); }, url: 'https://www.oree.com.ua' },
  { Logo: Handshake, logoSrc: '/logos/gpee.webp', brand: ['#0B1F5C', '#00B66B'], get name() { return i18n.t('ak_contacts_guaranteed_buyer_se'); }, url: 'https://www.gpee.com.ua' },
];

const STATE: OrgItem[] = [
  { Logo: Ministry, logoSrc: '/logos/mev.png', brand: ['#0057B8', '#F5C400'], logoTint: '#F5C400', get name() { return i18n.t('ak_contacts_ministry_of_energy_of_ukraine'); }, url: 'https://mev.gov.ua' },
  { Logo: Gavel, logoSrc: '/logos/nerc.svg', brand: ['#0057B8', '#F5C400'], logoTint: '#F5C400', get name() { return i18n.t('ak_contacts_neurc'); }, url: 'https://www.nerc.gov.ua' },
  { Logo: ShieldCheck, logoSrc: '/logos/sies.png', brand: ['#0057B8', '#F5C400'], get name() { return i18n.t('ak_contacts_state_energy_supervision_inspectorate'); }, url: 'https://sies.gov.ua' },
  { Logo: Flame, logoSrc: '/logos/dsns.svg', brand: ['#0057B8', '#FFD700'], get name() { return i18n.t('ak_contacts_state_emergency_service_2'); }, url: 'https://dsns.gov.ua' },
  { Logo: Leaf, logoSrc: '/logos/dei.png', brand: ['#0D7944', '#8FD6B2'], get name() { return i18n.t('ak_contacts_state_environmental_inspectorate'); }, url: 'https://www.dei.gov.ua' },
];

/** Домен без протоколу й слешів — у картці він читабельніший за повний URL. */
const hostOf = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');

function SectionTitle({ Icon, title, hint }: { Icon: LucideIcon; title: string; hint?: string }) {
  return (
    <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
      <Icon size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
      <h2 className="font-display" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{title}</h2>
      {hint && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{hint}</span>}
    </div>
  );
}

/**
 * Екстрена служба — КНОПКА-ІКОНКА, на яку наводиш, і спливає опис.
 *
 * Раніше це були чотири великі плитки з червоною заливкою й номером, які
 * займали цілий рядок сторінки. Тепер — чотири компактні кнопки в один ряд
 * (32px, radius 8, контрол кабінету §6), а подробиці — у спливній картці:
 * назва, коли звертатись (hint) і номер, який можна натиснути.
 *
 * ПОВЕДІНКА. Відкривається наведенням, фокусом з клавіатури й дотиком (на
 * телефоні наведення немає, тож клік перемикає картку). Картка стоїть
 * ВПРИТУЛ до кнопки (відступ усередині невидимої обгортки, а не margin), щоб
 * курсор міг дійти до номера, не вийшовши з зони наведення — інакше картка
 * зникала б, і на номер було б не натиснути. Escape закриває.
 *
 * ВИГЛЯД. Лише монохромна іконка (як вкладки-іконки в «Документах» і
 * «Керуванні»): квадрат 32px, червона обводка й червона іконка в усіх чотирьох.
 * Назви на кнопці немає — вона в спливній картці й в aria-label, тож доступна
 * і наведенням, і зчитувачу екрана. Червоний тут — свідома відмова від догми
 * «червоний лише для помилки»: це надзвичайна ситуація, її шукають поглядом.
 */
function EmergencyButton({ item }: { item: EmergencyItem }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      <button
        type="button"
        aria-label={item.label}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) setOpen(false); }}
        className="inline-flex items-center justify-center"
        style={{
          width: 32, height: 32, padding: 0, borderRadius: 8,
          background: open ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
          border: '1px solid var(--err)',
          cursor: 'pointer',
        }}
      >
        <item.Icon size={16} strokeWidth={2} style={{ color: 'var(--err)' }} />
      </button>

      {open && (
        // paddingTop — це «міст» між кнопкою і карткою: він частина обгортки,
        // тож наведення не переривається на шляху до номера.
        <div role="tooltip" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 30, paddingTop: 6 }}>
          <div style={{
            minWidth: 220, maxWidth: 280, padding: '12px 14px', borderRadius: 12,
            background: 'var(--surface-solid)', border: '1px solid var(--line-strong)',
            boxShadow: 'var(--shadow)',
          }}>
            <div className="font-display" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {item.label}
            </div>
            {item.hint && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>{item.hint}</div>
            )}
            <div style={{ marginTop: 10 }}>
              {item.phone ? (
                <a
                  href={`tel:${item.phone}`}
                  className="font-display tabular-nums"
                  style={{ fontSize: 22, fontWeight: 700, color: item.urgent ? 'var(--err)' : 'var(--text)', textDecoration: 'none' }}
                >
                  {item.phone}
                </a>
              ) : (
                <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{t('ak_contacts_number_not_specified')}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Картка організації: знак на 1/3 ширини, текст на 2/3.
 *
 * Пропорція задана сіткою `1fr 2fr`, а не відсотками й не фіксованими px:
 * картки стоять у гнучкій сітці, тож третина має рахуватись від РЕАЛЬНОЇ
 * ширини картки на кожному екрані.
 *
 * Плитка знака — не квадратик усередині відступів, а повноцінна ліва колонка
 * на всю висоту картки, тож `.tile` лишається без padding, а відступи
 * перенесені всередину текстової колонки. Інакше під знаком і над ним
 * лишалась би порожня смуга, і «третина» читалась би як маленький значок з
 * великим полем.
 *
 * Розділяє колонки лінія `--line`, а не друга поверхня: §3 не дозволяє
 * плитку на плитці, тож ліва колонка — лише підкладка `--surface-2`.
 */
function OrgCard({ item }: { item: OrgItem }) {
  // Тон силуету логотипа залежить від теми — див. silhouette нижче.
  const { isDark } = useTheme();
  // МОНОХРОМ СИЛУЕТОМ, а не простим grayscale: знаки прийшли різні (градієнти,
  // герб #333, растр), і саме лише grayscale лишило б їх різними за щільністю.
  // brightness(0) зводить знак у суцільний силует, invert піднімає до потрібного
  // сірого; відсоток різний для тем.
  const silhouette = `grayscale(1) brightness(0) invert(${isDark ? 0.72 : 0.42})`;
  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer"
      className="tile block group org-card" style={{ textDecoration: 'none', minWidth: 0, padding: 0, overflow: 'hidden' }}>
      {/* Фірмовий фон: шар під вмістом, відкривається тією самою маскою
          зліва направо, що й колір логотипа (contacts.css). */}
      {item.brand && (
        <span className="org-card__brand" aria-hidden="true"
          style={{ background: `linear-gradient(135deg, ${item.brand[0]} 0%, ${item.brand[1] ?? item.brand[0]} 100%)` }} />
      )}
      <div className="org-card__grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', minHeight: 104 }}>

        {/* Колонка знака без власного тла й без роздільної лінії: знак лежить
            прямо на плитці картки. Третину ширини тримає сітка, тож окремої
            поверхні для цього не потрібно. */}
        <div className="flex items-center justify-center" style={{ padding: 8 }}>
          {/* Два шари знака прямо в розмітці (contacts.css): нижній сірий задає
              розмір, верхній кольоровий відкривається при наведенні на картку.
              Знак із власним полотном (logoFlat) не зводимо в силует — лише
              знебарвлюємо: полотно непрозоре, і силует став би однією плямою. */}
          {item.LogoMark
            ? (
              <span className="org-logo" style={{ width: '100%', maxHeight: 62 }}>
                <span className="org-logo__gray" style={{ display: 'inline-flex', width: '100%', filter: silhouette }}>
                  <item.LogoMark />
                </span>
                <span className="org-logo__color" style={{ display: 'inline-flex' }} aria-hidden="true">
                  <item.LogoMark />
                </span>
                <span className="org-logo__scan" aria-hidden="true" />
              </span>
            )
            : item.logoSrc
            ? (
              <span className="org-logo">
                <img className="org-logo__gray" src={item.logoSrc} alt=""
                  style={{
                    maxWidth: '100%', maxHeight: item.logoFlat ? 60 : 62, objectFit: 'contain',
                    filter: item.logoFlat ? 'grayscale(1) contrast(1.05)' : silhouette,
                  }} />
                {item.logoTint
                  ? (
                    <span className="org-logo__color" aria-hidden="true"
                      style={{
                        background: item.logoTint,
                        WebkitMaskImage: `url(${item.logoSrc})`, maskImage: `url(${item.logoSrc})`,
                        WebkitMaskSize: 'contain', maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center', maskPosition: 'center',
                      }} />
                  )
                  : <img className="org-logo__color" src={item.logoSrc} alt="" aria-hidden="true" />}
                <span className="org-logo__scan" aria-hidden="true" />
              </span>
            )
            : <item.Logo size={38} strokeWidth={1.4} style={{ color: 'var(--text-muted)' }} />}
        </div>

        <div className="flex flex-col justify-center" style={{ padding: '12px 14px', minWidth: 0 }}>
          {item.role && (
            <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginBottom: 3 }}>{item.role}</div>
          )}
          <div className="font-display" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.25 }}>
            {item.name}
          </div>
          <div className="flex items-center group-hover:underline" style={{ gap: 4, marginTop: 6 }}>
            <ExternalLink size={11} strokeWidth={2} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
            <span style={{
              fontSize: 11, color: 'var(--text-muted)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{hostOf(item.url)}</span>
          </div>
        </div>

      </div>
    </a>
  );
}

function OrgGrid({ items }: { items: OrgItem[] }) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
      {items.map((it) => <OrgCard key={`${it.name}-${it.url}-${it.role ?? ''}`} item={it} />)}
    </div>
  );
}

export default function ContactsPage() {
  const { t } = useTranslation();
  return (
    <DashboardLayout breadcrumb={[t('ak_contacts_contacts')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1440)}`} style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          <section style={{ marginBottom: 22 }}>
            <SectionTitle Icon={AlertTriangle} title={t('ak_contacts_emergencies')} hint={t('ak_contacts_24_7')} />
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              {EMERGENCY.map((it) => <EmergencyButton key={it.label} item={it} />)}
            </div>
          </section>

          <section style={{ marginBottom: 22 }}>
            <SectionTitle Icon={Building2} title={t('ak_contacts_counterparties')} />
            <OrgGrid items={COUNTERPARTIES} />
          </section>

          <section style={{ marginBottom: 22 }}>
            <SectionTitle Icon={Landmark} title={t('ak_contacts_market')} />
            <OrgGrid items={MARKET} />
          </section>

          <section>
            <SectionTitle Icon={Landmark} title={t('ak_contacts_state')} />
            <OrgGrid items={STATE} />
          </section>

        </div>
      </div>
    </DashboardLayout>
  );
}
